/**
 * Ball-by-ball commentary.
 *
 * The prototype's lines were hand-written prose for seven deliveries. A match
 * is around 250, so they have to be generated — but generated text that repeats
 * is worse than terse text that does not, because repetition is what makes a
 * feed read as a machine.
 *
 * Three rules hold it together:
 *
 *   1. **Deterministic.** Phrasing is drawn from the delivery itself, never
 *      from Math.random. The same seed must produce the same match on the
 *      client and on the server that replays it; commentary is not part of
 *      that check today, but a feed that changed on replay would make any
 *      future desync impossible to read.
 *   2. **Never contradict the scorecard.** The line describes the event it was
 *      given and nothing else. No invented fielders, no invented deliveries.
 *   3. **Say nothing about the model.** No probabilities, no ratings, no
 *      "he was always likely to". A standing constraint on this project: the
 *      simulation is never explained to the user.
 */

import type { BallEvent } from '@ipl-sim/engine-cricket';

/**
 * A small deterministic hash over the delivery's identity, used to pick
 * phrasing. Two deliveries that differ in any visible way get different text.
 */
function variant(e: BallEvent, salt: number): number {
  // Math.imul throughout, and a murmur-style finalizer. A first version used
  // plain `*` with 32-bit constants, whose products exceed 2^53 and lose the
  // low bits -- the only ones the modulo below reads -- before the XOR sees
  // them.
  //
  // Measured, both spread about equally over an innings (chi2 10.1 vs 8.9 over
  // eight buckets) and over consecutive dots. So this is a correctness fix to
  // how the hash is built, not a measured cure for repetition: eight phrasings
  // will visibly repeat inside an over no matter how well they are mixed. If
  // repetition is the complaint, the answer is more phrasings.
  let h = salt | 0;
  h = Math.imul(h ^ (e.overIndex + 1), 0x27d4eb2d);
  h = Math.imul(h ^ e.ballInOver, 0x165667b1);
  h = Math.imul(h ^ e.score.runs, 0x85ebca6b);
  h = Math.imul(h ^ e.score.wickets, 0xc2b2ae35);
  h = Math.imul(h ^ e.strikerCard.balls, 0x27d4eb2d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491);
  h ^= h >>> 13;
  return h >>> 0;
}

const pick = <T>(items: readonly T[], e: BallEvent, salt: number): T =>
  items[variant(e, salt) % items.length];

/**
 * Openers describe the delivery, and the outcome phrase that follows describes
 * the shot — so these have to stay neutral about *where* the ball went. Naming
 * a line ("wide of off stump") produced lines like "Wide of off stump, tucked
 * off the hip", which contradict themselves. Length and pace are safe; line is
 * not, because nothing in a BallEvent records it.
 */
const LENGTHS = [
  'Full and straight',
  'Back of a length',
  'On a good length',
  'Fuller, angled in',
  'Short of a length',
  'Slower ball, well disguised',
  'Length ball on middle',
  'Quicker, skidding on',
  'Into the pitch, climbing',
  'Full, searching for the yorker',
];

const DOTS = [
  'defended into the covers.',
  'pushed to mid-on, no run.',
  'beaten outside off.',
  'dug out to the bowler.',
  'left alone.',
  'straight to point.',
  'squeezed to backward point, nothing there.',
  'blocked. Good ball.',
];

const ONES = [
  'worked away to square leg for one.',
  'nudged to third man, single taken.',
  'pushed to long-on, they cross.',
  'tucked off the hip, one to fine leg.',
  'driven to sweeper cover, easy single.',
];

const TWOS = [
  'clipped into the gap, back for two.',
  'driven to deep cover, two taken.',
  'worked into the deep, comfortable two.',
];

const FOURS = [
  'and that is beautifully timed through the covers.',
  'short and pulled hard, one bounce over midwicket.',
  'driven straight past the bowler. No chance.',
  'edged, but wide of slip and away to third man.',
  'punched off the back foot, races to the rope.',
  'flicked off the pads, fine leg has no hope.',
];

const SIXES = [
  'and that is enormous — straight back over long-on.',
  'picked up over deep square leg and into the crowd.',
  'slog-swept, and it clears the rope comfortably.',
  'down the ground, high and handsome.',
  'over extra cover. Outrageous shot.',
];

const WICKETS: Record<string, readonly string[]> = {
  bowled: ['through the gate, and the stumps are shattered.', 'castled. The off stump is out of the ground.'],
  lbw: ['struck on the pad, plumb in front. Given.', 'trapped on the crease, and the finger goes up.'],
  caught: ['the top edge goes up, and it is taken.', 'skied it, and the catch is held.', 'finds the fielder in the deep.'],
  'caught and bowled': ['a leading edge, and the bowler takes it himself.', 'straight back at the bowler, held low.'],
  stumped: ['beaten in the flight, and the bails are off in a flash.', 'dragged out of the crease and stumped.'],
  'run out': ['a terrible mix-up, and there is no way back.', 'direct hit, and he is well short.'],
  'hit wicket': ['and he has trodden on his own stumps.'],
  'obstructing the field': ['and he is given out obstructing the field.'],
};

const MILESTONES: Record<string, (e: BallEvent) => string> = {
  FIFTY: (e) => `${e.striker.name} brings up his fifty, off ${e.strikerCard.balls} balls`,
  HUNDRED: (e) => `${e.striker.name} reaches a hundred, off ${e.strikerCard.balls} balls`,
  PARTNERSHIP_FIFTY: (e) => `The stand is worth ${e.partnership.runs} off ${e.partnership.balls}`,
  MAIDEN_OVER: () => 'A maiden over. Rare, and worth its weight here.',
  BOWLER_THREE_FOR: (e) => `Three for ${e.bowler.name}`,
  HAT_TRICK: (e) => `A HAT-TRICK for ${e.bowler.name}!`,
};

/** The "17.4" style marker. Extras repeat the previous ball number, as scorecards do. */
export function ballNumber(e: BallEvent): string {
  return `${e.overIndex}.${e.ballInOver}`;
}

export type BallTone = 'four' | 'six' | 'out' | 'extra' | 'plain';

export function toneOf(e: BallEvent): BallTone {
  if (e.wicket) return 'out';
  if (e.runsOffBat === 6) return 'six';
  if (e.runsOffBat === 4) return 'four';
  if (e.extra) return 'extra';
  return 'plain';
}

export interface CommentaryLine {
  readonly tone: BallTone;
  /** The shouted opener — "SIX.", "OUT!" — or empty for an ordinary ball. */
  readonly lead: string;
  readonly text: string;
  /** Milestone footnote, when this delivery produced one. */
  readonly note?: string;
}

export function describe(e: BallEvent): CommentaryLine {
  const tone = toneOf(e);
  const length = pick(LENGTHS, e, 1);
  const note = e.milestone ? MILESTONES[e.milestone]?.(e) : undefined;

  if (e.wicket) {
    const lines = WICKETS[e.wicket.kind] ?? ['and that is the wicket.'];
    const how =
      e.wicket.kind === 'caught' && e.wicket.fielder
        ? `${pick(lines, e, 2)} ${e.wicket.fielder.name} takes it.`
        : pick(lines, e, 2);
    return {
      tone,
      lead: 'OUT!',
      text: `${length}, ${how}`,
      // The dismissed batter's figures matter more here than any milestone.
      note: `${e.striker.name} ${e.strikerCard.runs} (${e.strikerCard.balls})`,
    };
  }

  if (e.extra) {
    const kind = e.extra.kind;
    if (kind === 'wide') return { tone, lead: 'WIDE.', text: `${length}, and it is called wide.`, note };
    if (kind === 'noball') {
      return { tone, lead: 'NO BALL.', text: `${length} — over the line. Free hit coming.`, note };
    }
    if (kind === 'bye') return { tone, lead: '', text: `${length}, through to the keeper. ${e.extra.runs} bye${e.extra.runs > 1 ? 's' : ''}.`, note };
    return { tone, lead: '', text: `${length}, off the pad and away. ${e.extra.runs} leg bye${e.extra.runs > 1 ? 's' : ''}.`, note };
  }

  if (e.runsOffBat === 6) return { tone, lead: 'SIX.', text: `${length}, ${pick(SIXES, e, 3)}`, note };
  if (e.runsOffBat === 4) return { tone, lead: 'FOUR.', text: `${length}, ${pick(FOURS, e, 4)}`, note };
  if (e.runsOffBat === 2 || e.runsOffBat === 3) {
    return { tone, lead: '', text: `${length}, ${pick(TWOS, e, 5)}`, note };
  }
  if (e.runsOffBat === 1) return { tone, lead: '', text: `${length}, ${pick(ONES, e, 6)}`, note };

  // A dot with pressure building is worth remarking on; three in a row is when
  // a commentator would actually mention it.
  const pressure = e.dotsInARow >= 3 ? ` That is ${e.dotsInARow} dots in a row.` : '';
  return { tone, lead: '', text: `${length}, ${pick(DOTS, e, 7)}${pressure}`, note };
}

/** "4-0-31-2" — the conventional figures string. */
export function figures(balls: number, runs: number, wickets: number, maidens?: number): string {
  const overs = `${Math.floor(balls / 6)}${balls % 6 ? `.${balls % 6}` : ''}`;
  return maidens === undefined
    ? `${overs}-${runs}-${wickets}`
    : `${overs}-${maidens}-${runs}-${wickets}`;
}

/** "17.4" from a count of legal balls. */
export function oversFrom(legalBalls: number): string {
  return `${Math.floor(legalBalls / 6)}.${legalBalls % 6}`;
}
